"use client";

import { useEffect, useRef } from "react";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";

/**
 * Makes system-wide announcements land WITHOUT a refresh.
 *
 * Three triggers, cheapest first:
 *  1. Supabase Realtime: any INSERT/UPDATE on `platform_announcements`, and on
 *     `agency_announcements` for the agent's own agency, fires `onChange`. This
 *     is the same client-side channel pattern Phone Numbers and A2P already
 *     use. It only delivers once both tables are in the `supabase_realtime`
 *     publication with a SELECT policy for `authenticated` (see vault note) —
 *     until then the channel simply stays quiet and the other two triggers
 *     carry it.
 *  2. Tab focus: coming back to the tab re-checks immediately.
 *  3. The caller's own short poll (kept as the floor).
 *
 * The backend SSE stream (`/realtime/stream`) is NOT used: EventSource can't
 * send a Bearer token and the route sits behind the JWT guard, which is why
 * the frontend never connected to it in the first place.
 */
export function useAnnouncementLiveRefresh(onChange: () => void) {
  const { user } = useUserContext();
  const userId: string | undefined = (user as any)?.user?.id;
  const agencyId: string | undefined =
    (user as any)?.currentAgency?.agency?.id || (user as any)?.currentAgency?.agencyId || (user as any)?.user?.agencyId;
  const cb = useRef(onChange);
  cb.current = onChange;

  // Tab focus → catch up.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") cb.current();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // Live changes → refetch.
  useEffect(() => {
    if (!userId) return;
    let mounted = true;
    let client: any = null;
    let channel: any = null;
    (async () => {
      try {
        const { getSupabaseClient } = await import("@/app/_module/services/supabaseClient");
        client = await getSupabaseClient();
        if (!mounted || !client) return;
        let ch = client
          .channel(`announcements-live-${userId}`)
          .on("postgres_changes", { event: "*", schema: "public", table: "platform_announcements" }, () => cb.current());
        if (agencyId) {
          ch = ch.on(
            "postgres_changes",
            { event: "*", schema: "public", table: "agency_announcements", filter: `agency_id=eq.${agencyId}` },
            () => cb.current(),
          );
        }
        channel = ch.subscribe();
      } catch {
        /* realtime unavailable — polling and focus still cover it */
      }
    })();
    return () => {
      mounted = false;
      try {
        if (channel && client) client.removeChannel(channel);
      } catch {
        /* ignore */
      }
    };
  }, [userId, agencyId]);
}
